// src/helpers/targetNavigator.js
//
// 배너와 홈 공지의 '이동 화면' → 목적지 라우팅 규칙.
// 두 기능은 관리자 화면에서 같은 선택지를 쓰지만 서버 enum 이름이 서로 달라
// (쿠폰: 배너 USER_COUPON / 공지 COUPON, 커뮤니티 글: 배너 NOTICE / 공지
// COMMUNITY) 각자 처리하면 한쪽만 조용히 무반응이 된다. 두 어휘를 여기서
// 하나로 모아 한 함수가 처리한다.
import { navigateToCategoryItem } from "./categoryNavigator.js";
import { userStore } from "../stores/user.store.ts";

const TYPE_ALIASES = {
  COUPON: "USER_COUPON",
  NOTICE: "COMMUNITY",
};

function normalizeType(type) {
  const upper = String(type ?? "").toUpperCase();
  return TYPE_ALIASES[upper] ?? upper;
}

/**
 * @param {object} target 배너 아이템 또는 홈 공지. 타입은 `type`(배너) 또는
 *   `target_type`(공지) 어느 쪽이든 받는다.
 * @param {function} navigate react-router 의 navigate
 * @param {Array} categories CATEGORY 대상 해석용 홈 카테고리 목록
 */
export function navigateToTarget(target, navigate, categories) {
  if (!target) return;

  switch (normalizeType(target.type ?? target.target_type)) {
    case "SERVICE":
      if (target.target_uuid) navigate(`/service/${target.target_uuid}`);
      break;

    case "PLACE":
      navigate("/halal-map");
      break;

    case "COMMUNITY":
      // 특정 글이 지정되지 않았으면 커뮤니티 목록으로.
      navigate(target.target_uuid ? `/community/${target.target_uuid}` : "/community");
      break;

    case "USER_COUPON":
      // 쿠폰은 회원 전용 — 비회원은 로그인부터.
      navigate(userStore.getState().user ? "/coupon" : "/sign-in");
      break;

    case "WEB":
      if (target.target_url) window.open(target.target_url, "_blank", "noopener,noreferrer");
      break;

    case "CATEGORY": {
      // 배너는 category_ids, 공지는 target_id 로 카테고리를 지목한다.
      // target_id 0 은 관리자 화면에서 대상을 안 고른 기본값 — 지정 없음으로 본다.
      const id = target.category_ids?.length > 0 ? target.category_ids[0] : target.target_id || null;
      if (id == null) {
        navigate("/category");
        break;
      }
      // App과 동일하게: 가리키는 카테고리가 internal_route(visa 등)나
      // external_url이면 카테고리 목록이 아니라 해당 목적지로 보낸다.
      const cat = (Array.isArray(categories) ? categories : []).find((c) => c?.id === id);
      if (cat && cat.type && cat.type !== "service") {
        navigateToCategoryItem(cat, navigate);
        break;
      }
      navigate(`/category?categoryId=${encodeURIComponent(String(id))}`);
      break;
    }

    default:
      break;
  }
}

/** 이동할 곳이 정해져 있는지 — '자세히 보기' 버튼 노출 판단에 쓴다. */
export function hasTarget(target) {
  if (!target) return false;

  const type = normalizeType(target.type ?? target.target_type);
  if (type === "USER_COUPON" || type === "PLACE") return true;

  return (
    target.target_uuid != null ||
    target.target_url != null ||
    !!target.target_id ||
    target.category_ids?.length > 0
  );
}
